import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DESK_APPEARANCE_LABELS,
  DESK_APPEARANCE_STORAGE_KEY,
  DEFAULT_DESK_APPEARANCE,
  nextDeskAppearance,
  parseDeskAppearance,
  type DeskAppearance,
} from "./appearance";

type AppearanceContextValue = {
  appearance: DeskAppearance;
  label: string;
  setAppearance: (next: DeskAppearance) => void;
  cycleAppearance: () => void;
};

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

function readStored(): DeskAppearance {
  try {
    return parseDeskAppearance(
      localStorage.getItem(DESK_APPEARANCE_STORAGE_KEY),
    );
  } catch {
    return DEFAULT_DESK_APPEARANCE;
  }
}

/** CSS-var appearance packs — plan/36 Calm · Raised · Ink. */
export function AppearanceProvider(props: { children: ReactNode }) {
  const [appearance, setAppearanceState] = useState<DeskAppearance>(() => {
    if (typeof window === "undefined") return DEFAULT_DESK_APPEARANCE;
    const next = readStored();
    document.documentElement.dataset.appearance = next;
    return next;
  });

  const setAppearance = useCallback((next: DeskAppearance) => {
    setAppearanceState(next);
    try {
      localStorage.setItem(DESK_APPEARANCE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const cycleAppearance = useCallback(() => {
    setAppearance(nextDeskAppearance(appearance));
  }, [appearance, setAppearance]);

  useEffect(() => {
    document.documentElement.dataset.appearance = appearance;
  }, [appearance]);

  const value = useMemo(
    () => ({
      appearance,
      label: DESK_APPEARANCE_LABELS[appearance],
      setAppearance,
      cycleAppearance,
    }),
    [appearance, setAppearance, cycleAppearance],
  );

  return (
    <AppearanceContext.Provider value={value}>
      {props.children}
    </AppearanceContext.Provider>
  );
}

export function useDeskAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error("useDeskAppearance requires AppearanceProvider");
  }
  return ctx;
}
